import { InboxConversationSummary } from '@spike/omnichannel-model';
import { faPaperclip } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useAuth } from 'hooks';
import moment from 'moment-timezone';
import React from 'react';
import ChannelBadge from '../ChannelBadge/ChannelBadge';
import InboxAvatar from '../InboxAvatar/InboxAvatar';
import styles from './ConversationListItem.module.scss';

interface ConversationListItemProps {
    conversation: InboxConversationSummary;
    selected: boolean;
    isNew?: boolean;
    onSelect: (id: number) => void;
}

const formatTimestamp = (isoDate: string, timeZone: string): string => {
    const date = moment(isoDate).tz(timeZone);
    const now = moment().tz(timeZone);
    if (date.isSame(now, 'day')) return date.format('h:mm a');
    if (date.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
    return date.format('MMM D');
};

export const ConversationListItem: React.FC<ConversationListItemProps> = ({
    conversation,
    selected,
    isNew,
    onSelect
}) => {
    const auth = useAuth();
    const lastMessage = conversation.lastMessage;
    // an email whose body was only an inline image leaves an empty preview once the placeholder is gone
    const previewIsEmpty = lastMessage.preview.trim().length === 0;
    const hasUnread = conversation.unreadCount > 0;

    return (
        <button
            type="button"
            className={clsx(styles.root, selected && styles.selected, isNew && styles.isNew)}
            onClick={() => onSelect(conversation.id)}
        >
            <InboxAvatar
                name={conversation.contact.name}
                isProspect={conversation.contact.type === 'prospect'}
            />

            <div className={styles.body}>
                <div className={styles.topRow}>
                    <span className={clsx(styles.name, hasUnread && styles.nameUnread)}>
                        {conversation.contact.name}
                    </span>
                    <span className={styles.time}>{formatTimestamp(lastMessage.sentAt, auth.timeZone!)}</span>
                </div>

                <div className={styles.bottomRow}>
                    <span className={clsx(styles.preview, hasUnread && styles.previewUnread)}>
                        {lastMessage.direction === 'outbound' && <span className={styles.you}>You: </span>}
                        {previewIsEmpty ? (
                            <>
                                <FontAwesomeIcon
                                    icon={faPaperclip}
                                    className={styles.attachmentIcon}
                                />
                                Attachment
                            </>
                        ) : (
                            lastMessage.preview
                        )}
                    </span>

                    <span className={styles.meta}>
                        <ChannelBadge channel={lastMessage.channel} />
                        {hasUnread && <span className={styles.unread}>{conversation.unreadCount}</span>}
                    </span>
                </div>
            </div>
        </button>
    );
};

export default ConversationListItem;
